import { Menu as BaseMenu } from '@base-ui/react/menu'
import type { FC, HTMLAttributes, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import styles from './menu.module.css'

/** Tracks whether we're inside a Menu.Root so MenuItem can self-wrap when standalone */
const MenuContext = createContext(false)

export interface MenuProps {
  open: boolean
  onClose?: () => void
  anchorEl?: HTMLElement | null
  anchorOrigin?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' | 'center' }
  className?: string
  id?: string
  style?: React.CSSProperties
  children?: ReactNode
  /** @deprecated Ignored - kept for MUI compat */
  keepMounted?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  MenuListProps?: Record<string, unknown>
}

export const Menu: FC<MenuProps> = ({
  open,
  onClose,
  anchorEl,
  className,
  children,
  style,
  id,
  keepMounted: _keepMounted,
  MenuListProps,
}) => {
  const classes = [styles.menu, className].filter(Boolean).join(' ')

  return (
    <MenuContext.Provider value={true}>
      <BaseMenu.Root
        open={open}
        onOpenChange={o => {
          if (!o) {
            onClose?.()
          }
        }}
      >
        <BaseMenu.Portal>
          <BaseMenu.Positioner anchor={anchorEl ?? undefined} style={{ zIndex: 'var(--tuna-zIndex-modal)' as never }}>
            <BaseMenu.Popup className={classes} style={style} id={id} {...MenuListProps}>
              {children}
            </BaseMenu.Popup>
          </BaseMenu.Positioner>
        </BaseMenu.Portal>
      </BaseMenu.Root>
    </MenuContext.Provider>
  )
}

export interface MenuItemProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  disabled?: boolean
  dense?: boolean
  value?: string | number
  ref?: React.Ref<HTMLElement>
}

const menuItemClasses = (className?: string, selected?: boolean, _disabled?: boolean) =>
  [styles.item, selected ? styles.selected : undefined, className].filter(Boolean).join(' ')

/** MenuItem that renders as BaseMenu.Item inside a Menu, or a plain div when standalone */
export const MenuItem: FC<MenuItemProps> = ({
  selected,
  disabled,
  value,
  className,
  onClick,
  onKeyDown,
  dense: _dense,
  ref,
  ...rest
}) => {
  const insideMenu = useContext(MenuContext)

  if (insideMenu) {
    return (
      <BaseMenu.Item
        ref={ref}
        className={menuItemClasses(className, selected, disabled)}
        disabled={disabled}
        data-value={value}
        onClick={disabled ? undefined : onClick}
        {...rest}
      />
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
    }
    onKeyDown?.(e)
  }

  return (
    <div
      ref={ref as React.Ref<HTMLDivElement>}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      className={menuItemClasses(className, selected, disabled)}
      aria-disabled={disabled || undefined}
      data-value={value}
      onClick={disabled ? undefined : onClick}
      onKeyDown={disabled ? undefined : handleKeyDown}
      {...rest}
    />
  )
}
